/**
 * `useConversationViews(conversationId)` — TanStack Query wrapper for
 * `GET /api/conversations/:id/views` (builder-ssot-consolidation Phase 5
 * Session 5.1).
 *
 * The unified SSOT projection: `inputs` (ProvenanceRecord map — value +
 * provenance metadata), `cellValues`, `modelLayout`, `signal`, `dealMetadata`,
 * `aerialLatLng`, `meta`. Replaces the deprecated `/model-view` +
 * `/conversations/:id` as the single data source for all input-reading UI.
 *
 * Cache strategy:
 *   - `enabled: !!conversationId` — null id never fires the query.
 *   - `staleTime: 0` — refetches on mount (matches `useModelView`); Realtime
 *     v2 deltas from S5.3 patch this cache granularly so full refetch on
 *     every cell edit is not required.
 *   - `gcTime: 5 * 60_000` — 5min retention after last subscriber unmounts.
 *
 * Key is nested under `conversations.all` via `conversations.views(id)` so
 * any `conversations.all` invalidation sweeps this cache (mirrors modelView).
 * ADR 0017.
 */
import { useQuery } from '@tanstack/react-query';
import type { ViewsResponse } from '@arcanum-hq/shared-types';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

export function useConversationViews(conversationId: string | null) {
  return useQuery<ViewsResponse>({
    queryKey: conversationId
      ? queryKeys.conversations.views(conversationId)
      : ['conversations', '__no-id__', 'views'],
    queryFn: () => api.getViews(conversationId!),
    enabled: !!conversationId,
    staleTime: 0, // refetch on mount (matches useModelView)
    gcTime: 5 * 60_000, // 5min
  });
}
